import React, { useEffect, useMemo, useState } from "react";
import { useGame } from "../game/em2c-store";
import ResourceFlowDetails from "./ResourceFlowDetails";
import { formatResourceAmount, formatResourceRate } from "../game/resourceUnits";
import {
  ChevronDown, ChevronUp, Pickaxe, Layers3, Cog, Droplet, Wind, Wheat,
  Package, Cpu, Wrench, Recycle, Boxes, Fuel, FlaskConical, Coins,
  Trash2, Mountain, Pin,
} from "lucide-react";

const META = {
  power: { label: "Power", icon: Cog },
  water: { label: "Clean Water", icon: Droplet },
  oxygen: { label: "Oxygen", icon: Wind },
  food: { label: "Food", icon: Wheat },
  raw_water: { label: "Raw Water", icon: Droplet },
  wastewater: { label: "Wastewater", icon: Droplet },
  metal_ore: { label: "Metal Ore", icon: Pickaxe },
  iron: { label: "Iron", icon: Layers3 },
  aluminum: { label: "Aluminum", icon: Layers3 },
  copper: { label: "Copper", icon: Layers3 },
  silicon: { label: "Silicon", icon: Layers3 },
  refined_metal: { label: "General Metal", icon: Layers3 },
  regolith: { label: "Regolith", icon: Mountain },
  refinery_tailings: { label: "Refinery Tailings", icon: Mountain },
  construction_material: { label: "Building Material", icon: Boxes },
  processed_minerals: { label: "Processed Minerals", icon: Layers3 },
  polymers: { label: "Polymers", icon: Package },
  structural_components: { label: "Structural Components", icon: Boxes },
  machine_parts: { label: "Machine Parts", icon: Cog },
  electronic_components: { label: "Electronics", icon: Cpu },
  utility_components: { label: "Utility Components", icon: Wrench },
  fertilizer: { label: "Fertilizer", icon: Wheat },
  organic_waste: { label: "Organic Waste", icon: Recycle },
  waste: { label: "Waste", icon: Trash2 },
  recyclable_material: { label: "Recyclable Material", icon: Recycle },
  hydrogen: { label: "Hydrogen", icon: Wind },
  methane: { label: "Methane", icon: Wind },
  co2: { label: "CO₂", icon: Wind },
  nitrogen: { label: "Nitrogen", icon: Wind },
  argon: { label: "Argon", icon: Wind },
  rocket_propellant: { label: "Rocket Propellant", icon: Fuel },
  medical_supplies: { label: "Medical Supplies", icon: Package },
  mission_supplies: { label: "Mission Supplies", icon: Package },
  research_points: { label: "Research Points", icon: FlaskConical },
  credits: { label: "Credits", icon: Coins },
};

const GROUPS = [
  { id: "survival", label: "Survival", keys: ["power", "water", "oxygen", "food"] },
  { id: "water", label: "Water", keys: ["raw_water", "wastewater"] },
  { id: "mining", label: "Mining & Materials", keys: ["metal_ore", "iron", "aluminum", "copper", "silicon", "regolith", "refinery_tailings", "construction_material", "processed_minerals"] },
  { id: "industry", label: "Manufacturing", keys: ["polymers", "structural_components", "machine_parts", "electronic_components", "utility_components"] },
  { id: "recycle", label: "Closed Loop", keys: ["organic_waste", "waste", "recyclable_material", "fertilizer"] },
  { id: "gas", label: "Gas & Propellant", keys: ["hydrogen", "methane", "co2", "nitrogen", "argon", "rocket_propellant"] },
  { id: "colony", label: "Colony", keys: ["medical_supplies", "mission_supplies", "research_points", "credits"] },
];

const CHAINS = [
  { id: "industry", label: "Metal Ore → Local Manufacturing", keys: ["metal_ore", "iron", "machine_parts"] },
  { id: "water", label: "Closed-Loop Water", keys: ["raw_water", "water", "wastewater"] },
  { id: "gas", label: "Gas Recovery → Propellant", keys: ["hydrogen", "co2", "methane", "rocket_propellant"] },
  { id: "recycle", label: "Salvage → Reuse", keys: ["recyclable_material", "construction_material"] },
];

const DEFAULT_KEYS = ["metal_ore", "iron", "machine_parts", "construction_material"];
const PIN_STORAGE_KEY = "mars_resource_dock_pins_v2";
// CO is a real internal process stream but is deliberately not an everyday
// player storage stat. Retired resources never reappear in the live dock.
const HIDDEN_KEYS = new Set(["carbon_monoxide", "sorted_ore", "components", "building_modules", "ice", "raw_ore", "potable_water"]);

function labelFor(key) {
  return META[key]?.label || key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function getChainFor(key) {
  return CHAINS.find((chain) => chain.keys.includes(key)) || null;
}

function loadPinnedKeys() {
  try {
    const saved = localStorage.getItem(PIN_STORAGE_KEY);
    if (saved == null) return DEFAULT_KEYS;
    const parsed = JSON.parse(saved);
    if (!Array.isArray(parsed)) return DEFAULT_KEYS;
    return parsed.filter((key) => typeof key === "string" && !HIDDEN_KEYS.has(key));
  } catch (e) {
    return DEFAULT_KEYS;
  }
}

function ResourceValue({ resourceKey, compact = false, onClick, isPinned, onTogglePin }) {
  const resources = useGame((s) => s.resources);
  const netRates = useGame((s) => s.netRates || {});
  const closedLoopNetRates = useGame((s) => s._closedLoopNetRates || {});
  const value = resources?.[resourceKey];
  const meta = META[resourceKey] || { label: labelFor(resourceKey), icon: Package };
  const Icon = meta.icon || Package;
  if (!value || HIDDEN_KEYS.has(resourceKey)) return null;

  const net = Number(netRates?.[resourceKey] || 0) + Number(closedLoopNetRates?.[resourceKey] || 0);
  const rising = net >= 0;
  const rate = `${rising ? "▲ +" : "▼ "}${net.toFixed(1)}/s`;

  return (
    <div className={`group/stat relative flex items-stretch hover:bg-white/[0.06] transition-colors ${compact ? "border-r border-white/10 min-w-[178px]" : "w-full border border-white/[0.08] bg-white/[0.02]"}`} data-testid={`dock-resource-${resourceKey}`}>
      <button onClick={() => onClick?.(resourceKey)} className={`text-left flex-1 min-w-0 ${compact ? "pl-3 pr-1 py-2" : "pl-2.5 pr-1 py-1.5"}`}>
        <div className="flex items-center gap-2 min-w-0">
          <Icon size={13} className="text-slate-300 shrink-0" />
          <span className="text-[10px] font-mono uppercase tracking-wide text-slate-300 truncate">{meta.label}</span>
        </div>
        <div className="mt-0.5 flex items-baseline gap-2 font-mono">
          <span className="text-xs text-white">{formatResourceAmount(resourceKey, value.stored)}<span className="text-slate-600"> / {formatResourceAmount(resourceKey, value.max)}</span></span>
          <span className={`text-[9px] ${rising ? "text-[#00fa9a]" : "text-[#ff6b6b]"}`}>{formatResourceRate(resourceKey, net)}</span>
        </div>
      </button>
      <button onClick={(e) => { e.stopPropagation(); onTogglePin?.(resourceKey); }} className={`w-7 shrink-0 flex items-center justify-center transition-colors ${isPinned ? "text-cyan-400 bg-cyan-500/[0.08]" : "text-slate-600 hover:text-slate-300 hover:bg-white/[0.04]"}`} title={isPinned ? `Unpin ${meta.label}` : `Pin ${meta.label}`}>
        <Pin size={12} className={isPinned ? "fill-current" : ""} />
      </button>
    </div>
  );
}

export default function ResourceDock() {
  const [open, setOpen] = useState(false);
  const [activeGroup, setActiveGroup] = useState("all");
  const [focusKey, setFocusKey] = useState(null);
  const [pinnedKeys, setPinnedKeys] = useState(loadPinnedKeys);
  const resources = useGame((s) => s.resources);

  useEffect(() => {
    const close = () => { setOpen(false); setFocusKey(null); };
    window.addEventListener("mars:world-click", close);
    const otherMenu = (e) => { if (e.detail?.name !== "resources") close(); };
    window.addEventListener("mars:menu-open", otherMenu);
    const openRequested = (e) => { if (e.detail?.resourceKey) { setFocusKey(e.detail.resourceKey); setOpen(true); } };
    window.addEventListener("mars:open-resource", openRequested);
    return () => { window.removeEventListener("mars:world-click", close); window.removeEventListener("mars:menu-open", otherMenu); window.removeEventListener("mars:open-resource", openRequested); };
  }, []);

  const focusChain = focusKey ? getChainFor(focusKey) : null;
  const displayGroups = useMemo(() => {
    const assigned = new Set(GROUPS.flatMap((group) => group.keys));
    const ungrouped = Object.keys(resources || {}).filter((key) => !HIDDEN_KEYS.has(key) && !assigned.has(key)).sort((a, b) => labelFor(a).localeCompare(labelFor(b)));
    return ungrouped.length ? [...GROUPS, { id: "other", label: "Other", keys: ungrouped }] : GROUPS;
  }, [resources]);
  const visibleGroups = activeGroup === "all" ? displayGroups : displayGroups.filter((group) => group.id === activeGroup);
  const availablePinnedKeys = useMemo(() => pinnedKeys.filter((key) => !HIDDEN_KEYS.has(key) && resources?.[key]), [pinnedKeys, resources]);
  const activeResourceCount = useMemo(() => Object.keys(resources || {}).filter((key) => !HIDDEN_KEYS.has(key)).length, [resources]);

  const persistPins = (next) => {
    setPinnedKeys(next);
    try { localStorage.setItem(PIN_STORAGE_KEY, JSON.stringify(next)); }
    catch (e) { console.warn("[MarsColony] Could not save resource pins", e); }
  };
  const togglePin = (key) => {
    if (HIDDEN_KEYS.has(key)) return;
    persistPins(pinnedKeys.includes(key) ? pinnedKeys.filter((item) => item !== key) : [...pinnedKeys, key]);
  };
  const openResource = (key) => { window.dispatchEvent(new CustomEvent("mars:menu-open", { detail: { name: "resources" } })); setFocusKey(key); setOpen(true); };

  return (
    <div className="absolute bottom-0 left-14 right-3 md:right-80 z-[80] pointer-events-auto" style={{ fontFamily: "Rajdhani, sans-serif" }} data-testid="resource-dock">
      <style>{`
        .mars-resource-scroll { scrollbar-width: thin; scrollbar-color: rgba(0,229,255,.75) rgba(255,255,255,.055); }
        .mars-resource-scroll::-webkit-scrollbar { width: 6px; height: 6px; }
        .mars-resource-scroll::-webkit-scrollbar-track { background: rgba(255,255,255,.045); border-radius: 999px; }
        .mars-resource-scroll::-webkit-scrollbar-thumb { background: rgba(0,229,255,.8); border-radius: 999px; }
      `}</style>

      {open && (
        <div className="absolute bottom-full left-0 right-0 mb-1 h-[min(580px,65vh)] min-h-[240px] flex flex-col overflow-hidden bg-[#090C10] border border-cyan-400/20 shadow-[0_-10px_34px_rgba(0,0,0,0.55)]">
          <div className="mars-resource-scroll flex items-center gap-1 px-2.5 py-1.5 border-b border-white/10 overflow-x-auto shrink-0">
            <button onClick={() => setActiveGroup("all")} className={`px-2 py-1 text-[9px] font-mono uppercase tracking-widest border ${activeGroup === "all" ? "border-cyan-400 text-cyan-400 bg-cyan-500/10" : "border-white/10 text-slate-500"}`}>All</button>
            {displayGroups.map((group) => (
              <button key={group.id} onClick={() => setActiveGroup(group.id)} className={`px-2 py-1 text-[9px] font-mono uppercase tracking-widest border whitespace-nowrap ${activeGroup === group.id ? "border-cyan-400 text-cyan-400 bg-cyan-500/10" : "border-white/10 text-slate-500 hover:text-white"}`}>{group.label}</button>
            ))}
            <div className="ml-auto flex items-center gap-2 pl-3">
              <span className="text-[9px] font-mono uppercase tracking-widest text-slate-500 whitespace-nowrap">{activeResourceCount} stats · <Pin size={10} className="inline mx-1 text-cyan-400" />{pinnedKeys.length} pinned</span>
              <button onClick={() => setOpen(false)} className="px-2 py-1 text-slate-500 hover:text-white"><ChevronDown size={14} /></button>
            </div>
          </div>

          {focusChain && !focusKey && (
            <div className="px-2.5 py-1.5 border-b border-white/10 bg-cyan-500/[0.03] shrink-0">
              <div className="text-[9px] font-mono uppercase tracking-widest text-cyan-400 mb-1">{focusChain.label}</div>
              <div className="mars-resource-scroll flex items-center gap-1 overflow-x-auto pb-1">
                {focusChain.keys.map((key, index) => {
                  const val = resources?.[key];
                  return (
                    <React.Fragment key={`${key}-${index}`}>
                      {index > 0 && <span className="text-slate-600 text-xs">→</span>}
                      <button onClick={() => setFocusKey(key)} className={`px-2 py-1 border whitespace-nowrap ${focusKey === key ? "border-cyan-400/70 bg-cyan-500/10" : "border-white/10 bg-white/[0.02]"}`}>
                        <div className="text-[9px] font-mono text-slate-400">{labelFor(key)}</div>
                        <div className="text-[11px] font-mono text-white">{val ? Math.floor(val.stored) : 0}</div>
                      </button>
                    </React.Fragment>
                  );
                })}
              </div>
            </div>
          )}

          <div className="mars-resource-scroll flex-1 min-h-0 overflow-y-auto p-2.5">
            {focusKey && <div className="absolute inset-0 z-20 bg-[#0b111a] border border-cyan-400/40 shadow-2xl flex flex-col min-h-0"><div className="flex items-center justify-between px-4 py-3 border-b border-white/15 bg-[#151b25]"><span className="text-xs font-mono uppercase tracking-wider text-cyan-300">{labelFor(focusKey)} · Production & Consumption</span><button onClick={() => setFocusKey(null)} aria-label="Close resource details" className="px-3 py-1.5 border border-white/20 text-white hover:border-cyan-400">✕</button></div><div className="flex-1 min-h-0 overflow-y-auto p-4 mars-resource-scroll"><ResourceFlowDetails resourceKey={focusKey} label={labelFor(focusKey)} /></div></div>}
            {!focusKey && (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2.5">
              {visibleGroups.map((group) => {
                const keys = group.keys.filter((key) => !HIDDEN_KEYS.has(key) && resources?.[key]);
                if (!keys.length) return null;
                return (
                  <div key={group.id}>
                    <div className="text-[9px] font-mono uppercase tracking-[0.18em] text-slate-500 mb-1">{group.label}</div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
                      {keys.map((key) => (
                        <ResourceValue key={key} resourceKey={key} onClick={setFocusKey} isPinned={pinnedKeys.includes(key)} onTogglePin={togglePin} />
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
            )}
          </div>
        </div>
      )}

      <div className="h-14 flex items-stretch bg-[#0A0C10] border-t border-x border-white/10 shadow-[0_-8px_24px_rgba(0,0,0,0.3)] overflow-hidden">
        <button onClick={() => { if (!open) window.dispatchEvent(new CustomEvent("mars:menu-open", { detail: { name: "resources" } })); setOpen((value) => !value); }} className="w-28 shrink-0 px-3 border-r border-white/10 hover:bg-white/5 text-left" data-testid="toggle-resource-drawer">
          <div className="flex items-center gap-1 text-[9px] font-mono uppercase tracking-widest text-cyan-400">Resources {open ? <ChevronDown size={11} /> : <ChevronUp size={11} />}</div>
          <div className="text-[9px] font-mono text-slate-500 truncate">{activeResourceCount} stats · {pinnedKeys.length} pinned</div>
        </button>

        <div className="mars-resource-scroll flex-1 flex items-stretch overflow-x-auto">
          {availablePinnedKeys.length > 0 ? availablePinnedKeys.map((key) => (
            <ResourceValue key={key} resourceKey={key} compact onClick={openResource} isPinned onTogglePin={togglePin} />
          )) : (
            <button onClick={() => setOpen(true)} className="flex-1 px-4 text-left text-[10px] font-mono uppercase tracking-wider text-slate-500 hover:text-cyan-400 hover:bg-white/[0.03]">
              No resources pinned — open Resources and pin the stats you want to monitor
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
